import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button, Flex, Separator, Text } from "@radix-ui/themes";
import { Plus } from "lucide-react";
import { useExploreApi } from "@/v2/entities/explore/Explore.api";
import { useExploreColumns } from "@/v2/entities/explore/columns/useExploreColumns";
import { Table } from "@/v2/components/table/Table";
import {
  ExploreColumnType,
  ExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { useExploreState } from "@/v2/features/explore/useExploreState";
import { useStickySelectedInteraction } from "@/v2/features/explore/useStickySelectedInteraction";

import { Filter, mergeFilter } from "@/v2/entities/explore/Explore.filter";
import {
  parseFiltersFromFilterString,
  parseFiltersToFilterRequest,
} from "@/v2/features/explore-filter-menu/explore-filter.utils";
import {
  useExploreInteractions,
  useExploreAggInteractions,
} from "@/v2/entities/explore/Explore.api";
import { useExploreEnrichments } from "@/v2/features/explore/useExploreEnrichments";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { ExploreViewBySlugHeader } from "@/v2/features/explore/ExploreViewBySlugHeader";
import { SimulatorWrapper } from "@/routes/simulators/wrapper";
import { TicketView } from "@/v2/features/interaction-view/TicketView";
import { ConversationView } from "@/v2/features/interaction-view/ConversationView";
import { ExploreFiltersValue } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import {
  AllowedAggOrderBy,
  AllowedInteractionOrderBy,
} from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.model";
import {
  conversationsNotAllowedGroupBy,
  ticketsNotAllowedGroupBy,
} from "@/v2/features/explore/explore-display-options/ExploreDisplayOptions.consts";
import {
  isExploreAggColumnType,
  isExploreColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreChartWizard } from "@/v2/features/explore/ExploreMainChart";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "sonner";
import { useExploreCustomFields } from "@/v2/features/explore/useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "@/v2/features/explore/useExploreConversationCustomFieldsForView";

export function ExploreViewBySlug() {
  const { user } = useAuth0();
  const hasWidgets = useFlag("explore-widget", user?.owner === "quack");
  const navigate = useNavigate();
  const { getViewBySlug } = useExploreApi();
  const params = useParams();
  const viewSlug = params.slug as string;
  const [isStateReady, setIsStateReady] = useState(false);

  useEffect(() => {
    setIsStateReady(false);
  }, [viewSlug]);

  const { data: view, isLoading: viewLoading } = useQuery({
    queryKey: ["explore-view-by-slug", viewSlug],
    queryFn: () => getViewBySlug(viewSlug),
  });

  const notAllowedGroupByOptions = useMemo<ExploreGroupByColumnType[]>(() => {
    return view?.source === "conversations"
      ? conversationsNotAllowedGroupBy
      : ticketsNotAllowedGroupBy;
  }, [view?.source]);

  const exploreState = useExploreState({
    allowedGroupByOptions: [],
    notAllowedGroupByOptions,
    persist: false,
  });

  const {
    state: {
      groupBy,
      dateRange,
      filters: userFilters,
      sorting,
      parsedDateRange,
    },
    handleGroupClick,
    handleSortingChange,
    handleGroupByChange,
    handleFiltersChange: setUserFilters,
    handleClearAllFilters: clearUserFilters,
    handleDateRangeChange,
  } = exploreState;

  const [baseFilters, setBaseFilters] = useState<ExploreFiltersValue>({});

  const currentSorting = useMemo(() => {
    if (sorting && sorting.length > 0 && sorting[0].id) {
      return {
        id: sorting[0].id,
        desc: sorting[0].desc ?? true,
      };
    }
    if (view) {
      if (view.groupBy) {
        return {
          id: view.aggOrderBy ?? "volume",
          desc: view.aggOrderDir === "desc",
        };
      } else {
        return {
          id: view.orderBy ?? "createdAt",
          desc: view.orderDir === "desc",
        };
      }
    }
    return { id: "createdAt", desc: true };
  }, [sorting, view]);

  const interactionOrderByParams = useMemo(() => {
    const { id, desc } = currentSorting;
    const validInteractionOrderBy = isExploreAggColumnType(id)
      ? "createdAt"
      : id;

    return {
      orderBy: validInteractionOrderBy as AllowedInteractionOrderBy,
      orderDir: desc ? "desc" : ("asc" as "asc" | "desc"),
    };
  }, [currentSorting]);

  const aggOrderByParams = useMemo(() => {
    const { id, desc } = currentSorting;
    const validAggOrderBy =
      isExploreColumnType(id) && !isExploreAggColumnType(id) ? "volume" : id;

    return {
      orderBy: validAggOrderBy as AllowedAggOrderBy,
      orderDir: desc ? "desc" : ("asc" as "asc" | "desc"),
    };
  }, [currentSorting]);

  useEffect(() => {
    if (view && !isStateReady) {
      if (view.groupBy) {
        handleSortingChange([
          {
            id: view.aggOrderBy ?? "volume",
            desc: view.aggOrderDir === "desc",
          },
        ]);
      } else {
        handleSortingChange([
          {
            id: view.orderBy ?? "createdAt",
            desc: view.orderDir === "desc",
          },
        ]);
      }

      const viewGroupBy = view.groupBy ?? undefined;
      if (groupBy !== viewGroupBy) {
        handleGroupByChange(viewGroupBy);
      }

      if (view.filterString) {
        try {
          const parsed = parseFiltersFromFilterString(view.filterString);
          setBaseFilters(parsed);
        } catch (e) {
          console.error("Failed to parse view filter string", e);
          setBaseFilters({});
        }
      } else {
        setBaseFilters({});
      }

      setIsStateReady(true);
    }
  }, [view, isStateReady, groupBy, handleSortingChange, handleGroupByChange]);

  const enrichments = useExploreEnrichments();
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();

  const [search, setSearch] = useState("");
  const [selectedInteractionId, setSelectedInteractionId] = useState<string>();

  const searchFilter = useMemo<Filter | undefined>(() => {
    if (search) {
      const filter: Filter = {
        or: [
          { subject: { like: `%${search.toLowerCase()}%` } },
          { displayId: { like: `%${search.toLowerCase()}%` } },
          { tags: { contains: search.toLowerCase() } },
          { tenantUser: { like: `%${search.toLowerCase()}%` } },
        ],
      };
      return filter;
    }
  }, [search]);

  const combinedFilter = useMemo<Filter>(() => {
    let filter: Filter = {
      and: [
        { createdAt: { gte: parsedDateRange.dateFrom } },
        { createdAt: { lte: parsedDateRange.dateTo } },
      ],
    };

    if (Object.keys(baseFilters).length > 0) {
      filter = mergeFilter(filter, parseFiltersToFilterRequest(baseFilters));
    }

    if (Object.keys(userFilters).length > 0) {
      filter = mergeFilter(filter, parseFiltersToFilterRequest(userFilters));
    }

    if (searchFilter != null) {
      filter = mergeFilter(filter, searchFilter);
    }

    return filter;
  }, [parsedDateRange, baseFilters, userFilters, searchFilter]);

  const hiddenFilters = useMemo(() => {
    if (view?.source === "conversations") {
      return mergeFilter(combinedFilter, {
        and: [{ subject: { isnotnull: "true" } }],
      });
    }
    return combinedFilter;
  }, [view?.source, combinedFilter]);

  const safeSource = useMemo(() => {
    const src = view?.source;
    if (src === "interactions") return "tickets";
    if (src === "tickets" || src === "conversations") return src;
    return "tickets";
  }, [view?.source]);

  const customFieldsBySource = useMemo(
    () =>
      safeSource === "conversations"
        ? conversationCustomFieldsForView
        : customFields,
    [safeSource, customFields, conversationCustomFieldsForView]
  );

  const customFieldsColumns = useMemo(() => {
    return customFieldsBySource.filter((customField) =>
      view?.columns?.includes(customField.id as ExploreColumnType)
    );
  }, [customFieldsBySource, view?.columns]);

  const visibleColumnsForTable = useMemo(() => {
    if (!view?.columns) return undefined;
    const uniqueColumns = Array.from(new Set(view.columns));
    return uniqueColumns.filter(
      (column) =>
        !customFieldsBySource.some((customField) => customField.id === column)
    );
  }, [view?.columns, customFieldsBySource]);

  const { columns, aggColumns } = useExploreColumns({
    source: safeSource,
    groupBy: groupBy,
    enrichments: safeSource === "tickets" ? enrichments : [],
    customFields: customFieldsColumns,
    visibleColumns: visibleColumnsForTable,
    visibleAggColumns: view?.aggColumns,
  });

  const hasGroupFilter = useMemo(() => {
    if (!groupBy) return false;
    return userFilters.hasOwnProperty(groupBy);
  }, [combinedFilter, groupBy]);

  const exploreInteractionsQuery = useExploreInteractions({
    source: safeSource,
    orderBy: interactionOrderByParams.orderBy,
    orderDir: interactionOrderByParams.orderDir,
    filter: hiddenFilters,
    enabled:
      isStateReady &&
      (groupBy == null || hasGroupFilter) &&
      !!view &&
      !!view.source,
  });

  const exploreAggInteractionsQuery = useExploreAggInteractions({
    source: safeSource,
    groupBy,
    orderBy: aggOrderByParams.orderBy,
    orderDir: aggOrderByParams.orderDir,
    filter: hiddenFilters,
    enabled: isStateReady && groupBy != null && !!view && !!view.source,
  });

  const queryClient = useQueryClient();

  const handleInteractionClick = useCallback((row: ExploreInteractionRow) => {
    setSelectedInteractionId(row.displayId);
  }, []);

  const handleInteractionValidated = useCallback(() => {
    void queryClient.invalidateQueries(["explore-v2"]);
  }, [queryClient]);

  const selectedInteraction = useStickySelectedInteraction(
    selectedInteractionId,
    exploreInteractionsQuery.data
  );

  const handleFetchNextInteractions = useCallback(async () => {
    await exploreInteractionsQuery.fetchNextPage();
  }, [exploreInteractionsQuery]);

  const handleCreateView = useCallback(() => {
    navigate("/explore/views/new");
  }, [navigate]);

  if (viewLoading) {
    return (
      <Flex
        width="100%"
        height="100dvh"
        align="center"
        justify="center"
        style={{ backgroundColor: "var(--color-background)" }}
      >
        <Text>Loading view...</Text>
      </Flex>
    );
  }

  if (!view) {
    toast.warning("The view you are looking for does not exist", {
      id: "view-not-found",
    });
    return <Navigate to="/explore/views" replace />;
  }

  return (
    <>
      <Flex
        width={"100%"}
        maxHeight={"100dvh"}
        height={"100%"}
        direction="column"
        className="bg-background"
        overflow={"scroll"}
      >
        <ExploreViewBySlugHeader
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onSearchChange={setSearch}
          userFilters={userFilters}
          onUserFiltersChange={setUserFilters}
          onClearUserFilters={clearUserFilters}
          source={
            (view?.source === "interactions" ? "tickets" : view?.source) ||
            "tickets"
          }
          view={view}
          filter={hiddenFilters}
          search={search}
          isEvaluated={false}
          groupBy={groupBy}
          onGroupByChange={handleGroupByChange}
          allowedGroupByOptions={[]}
          notAllowedGroupByOptions={notAllowedGroupByOptions}
          conversationCustomFields={
            (view?.source === "interactions" ? "tickets" : view?.source) ===
            "conversations"
              ? conversationCustomFieldsForView
              : undefined
          }
          ticketCustomFields={
            (view?.source === "interactions" ? "tickets" : view?.source) ===
            "tickets"
              ? customFields
              : undefined
          }
        />

        <Separator
          orientation="horizontal"
          size="4"
          className="bg-[--q-border]"
        />

        <Flex flexShrink={"0"} className="h-[145px]" />

        {hasWidgets && (
          <Flex flexShrink={"0"} direction={"column"}>
            <ExploreChartWizard
              hiddenFilters={hiddenFilters}
              groupBy={groupBy}
              source={safeSource}
            />
          </Flex>
        )}

        {/* Table */}
        <Flex minHeight={"calc(100dvh - 102px)"} direction={"column"}>
          {(groupBy == null || hasGroupFilter) && (
            <Table
              data={exploreInteractionsQuery.data}
              columns={columns}
              loading={exploreInteractionsQuery.isLoading}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              hasNextPage={exploreInteractionsQuery.hasNextPage}
              isFetching={exploreInteractionsQuery.isFetching}
              isFetchingNextPage={exploreInteractionsQuery.isFetchingNextPage}
              fetchNextPage={handleFetchNextInteractions}
              onRowClicked={handleInteractionClick}
            />
          )}

          {groupBy != null && !hasGroupFilter && (
            <Table
              data={exploreAggInteractionsQuery.data?.results ?? []}
              columns={aggColumns}
              loading={exploreAggInteractionsQuery.isLoading}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              onRowClicked={handleGroupClick}
            />
          )}
        </Flex>
      </Flex>
      {selectedInteraction != null && (
        <>
          {view?.source === "tickets" && (
            <SimulatorWrapper>
              <TicketView
                key={selectedInteraction.displayId}
                ticket={selectedInteraction}
                enrichments={view?.source === "tickets" ? enrichments : []}
                customFields={view?.source === "tickets" ? customFields : []}
                allowValidate
                onTicketValidated={handleInteractionValidated}
                onClose={() => setSelectedInteractionId(undefined)}
              />
            </SimulatorWrapper>
          )}

          {view?.source === "conversations" && (
            <ConversationView
              key={selectedInteraction.displayId}
              conversation={selectedInteraction}
              enrichments={view?.source === "conversations" ? enrichments : []}
              customFields={
                view?.source === "conversations"
                  ? conversationCustomFieldsForView
                  : []
              }
              allowValidate
              onConversationValidated={handleInteractionValidated}
              onClose={() => setSelectedInteractionId(undefined)}
            />
          )}
        </>
      )}
    </>
  );
}
